import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  css,
  FormControl,
  InputLabel,
  Link,
  ListItemText,
  Select,
  styled,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  TextField,
  CircularProgress,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import {
  ExecutionResultsStatusFilterOptions,
  ExeReportHistoryStatus,
  TestSuiteStatusOptions,
} from "../../components-new/layout/constants";
import {
  AuthedFileLink,
  CustomSelect,
  IconContainer,
  PageTitle,
} from "../../components-new/common";
import { openAuthedResource } from "../../utils/authedResource";
import {
  CurrentExecutionIcon,
  DownloadIcon,
  HistoryIcon,
  LogFileIcon,
} from "../../components-new/icons";
import {
  useDownloadTestSuiteQuery,
  useExecuteTestSuitesMutation,
  useStopTestSuiteQuery,
  useUpdateIgnoreStatus,
  usePauseResumeTestSuiteMutation,
} from "../../useQuery";
import TestSuiteExecuteDialog from "./TestSuiteExecuteDialog";
import DoNotDisturbAltIcon from "@mui/icons-material/DoNotDisturbAlt";
import CustomDialog from "../../components-new/common/CustomDialog";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import PrettyReports from "./PrettyReports";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CompareRoundedIcon from "@mui/icons-material/CompareRounded";
import {
  BackupTableOutlined,
  PictureAsPdf,
  RunCircleOutlined,
  VideoLibraryOutlined,
} from "@mui/icons-material";
import { useNotification } from "../../hooks";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { maxHeight } from "@mui/system";
const StyledTableCell = styled(TableCell)`
  color: #232d3d;
  font-size: 14px;
  font-weight: 600;
  border-bottom: 1px solid #e2e6f3;
`;

const StyledTableBodyCell = styled(TableCell)`
  padding: 8px 15px;
  border-bottom: 1px solid #eee;
  p {
    color: #2b3539;
    font-size: 16px;
    font-weight: 500;
    padding-right: 15px;
    min-width: 60px;
  }
`;

const typeStyles = {
  PASS: css`
    border-radius: 41px;
    border: 0px solid #30ca17;
    background: #e7fbe4;
  `,
  FAIL: css`
    border-radius: 41px;
    border: 0px solid #e15151;
    background: #fbe7e7;
  `,
  FAILED: css`
    border-radius: 41px;
    border: 0px solid #e15151;
    background: #fbe7e7;
  `,
  STOP: css`
    border-radius: 41px;
    border: 0px solid #e15151;
    background: #fbe7e7;
  `,
  STOPP: css`
    border-radius: 41px;
    border: 0px solid #e15151;
    background: #fbe7e7;
  `,
  STOPPED: css`
    border-radius: 41px;
    border: 0px solid #e15151;
    background: #fbe7e7;
  `,
  ABORTED: css`
    border-radius: 41px;
    border: 0px solid #e15151;
    background: #fbe7e7;
  `,
  INCOMPLETE: css`
    border-radius: 41px;
    border: 0px solid #e15151;
    background: #fbe7e7;
  `,
  SKIPPED: css`
    border-radius: 41px;
    border: 0px solid #9e9e9e;
    background: #f5f5f5;
  `,
};

const StatusPill = styled(Chip)`
  min-width: 82px;
  padding: 8px 10px;
  gap: 10px;
  ${(props) => typeStyles[props.type]}
  span {
    text-transform: capitalize;
  }
`;

const CustomMenuItem = styled(MenuItem)(({ theme }) => ({
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
  "&.MuiButtonBase-root.MuiMenuItem-root": {
    display: "flex",
    padding: "10px 16px",
  },
  "& .MuiListItemIcon-root": {
    minWidth: "32px",
    color: "#242F3F",
    "& svg": {
      width: "18px",
      height: "18px",
    },
  },
  "& .MuiTypography-root": {
    fontSize: "14px",
    fontWeight: 500,
    color: "#2B3539",
  },
}));

const ExecutionReportsTestSuiteTable = ({
  data,
  isSuccess,
  testSuiteName,
  prod,
  setPageno,
  pageNO,
  totalPageno,
  statusFilter = [],
  runByFilter = [],
  runByOptions = [],
  dateFilter = "",
  onStatusFilterChange,
  onRunByFilterChange,
  onDateFilterChange,
  onClearFilters,
  isPrettyView = true,
  setIsPrettyView = () => {},
  isLoadingResults = false,
}) => {
  const navigate = useNavigate();
  const componentRef = useRef();
  const [statusType, setStatusType] = useState({});
  const [testSuiteId, setTestSuiteId] = useState("");
  const [downloadType, setDownloadType] = useState("");
  const [enableDownload, setEnableDownload] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("ALL");
  const [selectedItemForExecute, setSelectedItemForExecute] = useState(null);
  const [openStopDialog, setOpenStopDialog] = useState(false);
  const [prettyItem, setPrettyItem] = useState(data[0] || null);
  const [ignoredStatuses, setIgnoredStatuses] = useState({});
  const [selectedIdForIgnoreUpdate, setSelectedIdForIgnoreUpdate] = useState(null);
  const [ignoreStatus, setIgnoreStatus] = useState(false);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const [downloadMenuItemId, setDownloadMenuItemId] = useState(null);
  const notify = useNotification();
  const hasActiveFilters = Boolean(
    (statusFilter && statusFilter.length > 0) || (runByFilter && runByFilter.length > 0) || dateFilter,
  );
  const isPretty = isPrettyView;
  const hasData = Array.isArray(data) && data.length > 0;

  useEffect(() => {
    if (data && data.length > 0) {
      setPrettyItem(data[0]);

      const initialStatuses = {};
      data.forEach((item) => {
        initialStatuses[item.idtest_suite_results] = !!item.isIgnored;
      });
      setIgnoredStatuses(initialStatuses);
    } else {
      setPrettyItem(null);
      setIgnoredStatuses({});
    }
  }, [data]);

  const selectedTestSuite = useSelector((state) => state.executionSlice.selectedTestSuite);
  const productId = useSelector((state) => state.executionSlice.selectedProduct);
  const { refetch: refetchDownloadTestSuite } = useDownloadTestSuiteQuery(
    testSuiteId,
    downloadType,
  );
  const { refetch: handleStopTSExecution } = useStopTestSuiteQuery(
    selectedItemForExecute?.idtest_suite_results,
    "suite",
  );

  const { mutate: updateIgnoreStatusMutation } = useUpdateIgnoreStatus(
    ignoreStatus,
    selectedIdForIgnoreUpdate,
    {
      onSuccess: (data) => {
        notify(`Successfully ${ignoreStatus ? "ignored" : "unignored"} test suite`, "success");
      },
      onError: (error) => {
        notify("Error updating status", "error");
        if (selectedIdForIgnoreUpdate) {
          setIgnoredStatuses((prev) => ({
            ...prev,
            [selectedIdForIgnoreUpdate]: !ignoreStatus,
          }));
        }
      },
    },
  );

  const handleOpenLogFile = (item) => {
    if (!item?.logfile) {
      notify("No log file is available for this run.", "error");
      return;
    }

    openAuthedResource(item.logfile, {
      filename: `${item?.testsuitename || testSuiteName || "test-suite"}.log`,
    }).catch((error) => notify(error.message || "Failed to open the log file.", "error"));
  };

  useEffect(() => {
    if (enableDownload && testSuiteId && downloadType) refetchDownloadTestSuite();
    setEnableDownload(false);
    setTestSuiteId("");
    setDownloadType("");
  }, [enableDownload, refetchDownloadTestSuite, downloadType, testSuiteId]);

  useEffect(() => {
    if (selectedIdForIgnoreUpdate) {
      updateIgnoreStatusMutation();
    }
  }, [selectedIdForIgnoreUpdate, ignoreStatus, updateIgnoreStatusMutation]);

  const { mutate: handleExecuteTestSuite, isSuccess: isExecuteTestSuiteSuccess } =
    useExecuteTestSuitesMutation();

  const { mutate: pauseResumeTestSuite } = usePauseResumeTestSuiteMutation();

  const handleTestCaseHistory = (item, criteriaData) => {
    setTestSuiteId(item.idtest_suite_results);
    const url = `/new/reports/history?tsId=${item.idtest_suite_results}&criteria=${criteriaData}&name=${testSuiteName}&date=${item.Executed_Date}`;
    window.open(url, "_blank");
  };

  const handleBenchmarkScenarios = (item) => {
    setTestSuiteId(item.idtest_suite_results);
    const url = `/new/reports/benchmark?tsrId=${item.idtest_suite_results}&name=${testSuiteName}&date=${item.Executed_Date}`;
    window.open(url, "_blank");
  };

  const handleViewRunHistory = (item) => {
    const url = `/new/test-case-history?tsId=${item.Test_Suite_Id}&name=${testSuiteName}&date=${item.Executed_Date}&tsrid=${item.idtest_suite_results}&prodID=${productId}`;

    window.open(url, "_blank");
  };

  const handleOpenDialog = (item) => {
    setSelectedItemForExecute(item);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleStopExecution = (item) => {
    setSelectedItemForExecute(item);
    setOpenStopDialog(true);
  };

  const handleConfirmStopExecution = () => {
    handleStopTSExecution();
    setOpenStopDialog(false);
    navigate("/new/suite-reports", {
      state: {
        isExecuteClick: true,
        selectedSuit: selectedTestSuite,
        product: productId,
        fetchTestSuite: true,
      },
    });
  };

  const handleCancelStopExecution = () => {
    setOpenStopDialog(false);
  };

  const handleToggleIgnored = (testSuiteResultId, currentStatus) => {
    const newStatus = !currentStatus;

    setIgnoredStatuses((prev) => ({
      ...prev,
      [testSuiteResultId]: newStatus,
    }));

    setSelectedIdForIgnoreUpdate(testSuiteResultId);
    setIgnoreStatus(newStatus);
  };

  const handlePauseResumeTestSuite = (testSuiteResultId, message) => {
    if (testSuiteResultId && message) {
      pauseResumeTestSuite({ tsrid: testSuiteResultId, message });
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", width: "100%", gap: 2 }}>
      <Box display="flex" justifyContent="space-between" flexShrink={0}>
        <PageTitle title={"Execution Summary"} />

        <Box display="flex" alignItems={"center"} gap={2}>
          {!isPretty && (
            <Box display="flex" alignItems="flex-end" gap={1.5}>
              <Box>
                <Typography color="#8089A8" fontSize="14px" fontWeight={500}>
                  Run Date
                </Typography>
                <TextField
                  type="date"
                  size="small"
                  value={dateFilter}
                  onChange={(e) => onDateFilterChange?.(e.target.value)}
                  sx={{
                    "& .MuiInputBase-root": {
                      minHeight: "54px",
                      borderRadius: "5px",
                      backgroundColor: "#ffffff",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D1DBFF",
                    },
                  }}
                />
              </Box>

              <Box>
                <Typography color="#8089A8" fontSize="14px" fontWeight={500}>
                  Status
                </Typography>
                <FormControl size="small" sx={{ minWidth: "170px" }}>
                  <Select
                    multiple
                    displayEmpty
                    value={statusFilter}
                    onChange={(e) => onStatusFilterChange?.(e.target.value)}
                    renderValue={(selected) => (selected.length ? selected.join(", ") : "All Status")}
                    sx={{
                      height: "54px",
                      borderRadius: "5px",
                      background: "#ffffff",
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#D1DBFF" },
                    }}
                    MenuProps={{ PaperProps: { style: { maxHeight: 250, overflowY: "auto" } } }}
                  >
                    {ExecutionResultsStatusFilterOptions.filter((opt) => opt.value !== "ALL").map(
                      (opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          <Checkbox checked={statusFilter.indexOf(opt.value) > -1} />
                          <ListItemText primary={opt.label} />
                        </MenuItem>
                      ),
                    )}
                  </Select>
                </FormControl>
              </Box>

              <Box>
                <Typography color="#8089A8" fontSize="14px" fontWeight={500}>
                  Run By
                </Typography>
                <FormControl size="small" sx={{ minWidth: "170px" }}>
                  <Select
                    multiple
                    displayEmpty
                    value={runByFilter}
                    onChange={(e) => onRunByFilterChange?.(e.target.value)}
                    renderValue={(selected) => (selected.length ? selected.join(", ") : "All")}
                    sx={{
                      height: "54px",
                      borderRadius: "5px",
                      background: "#ffffff",
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#D1DBFF" },
                    }}
                    MenuProps={{ PaperProps: { style: { maxHeight: 250, overflowY: "auto" } } }}
                  >
                    {runByOptions.map((email) => (
                      <MenuItem key={email} value={email}>
                        <Checkbox checked={runByFilter.indexOf(email) > -1} />
                        <ListItemText primary={email} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Button
                variant="outlined"
                onClick={onClearFilters}
                disabled={!hasActiveFilters}
                sx={{
                  textTransform: "capitalize",
                  height: "54px",
                  borderColor: "#D1DBFF",
                }}
              >
                Clear
              </Button>
            </Box>
          )}
          {isPretty && prettyItem && (
            <StyledTableBodyCell>
              <Box display={"flex"} columnGap={1}>
                {(prettyItem.Status === "STARTED" || prettyItem.Status === "RUNNING") && (
                  <IconContainer
                    tooltipText={"Stop"}
                    handleClick={() => handleStopExecution(prettyItem)}
                  >
                    <DoNotDisturbAltIcon />
                  </IconContainer>
                )}
                {(prettyItem.Status === "STARTED" || prettyItem.Status === "RUNNING") && (
                  <IconContainer
                    tooltipText={"Pause"}
                    handleClick={() =>
                      handlePauseResumeTestSuite(prettyItem.idtest_suite_results, "pause")
                    }
                  >
                    <PauseIcon />
                  </IconContainer>
                )}
                {prettyItem.Status === "PAUSED" && (
                  <IconContainer
                    tooltipText={"Resume"}
                    handleClick={() =>
                      handlePauseResumeTestSuite(prettyItem.idtest_suite_results, "resume")
                    }
                  >
                    <PlayArrowIcon />
                  </IconContainer>
                )}

                <IconContainer
                  tooltipText={"Download Options"}
                  handleClick={(e) => {
                    setDownloadMenuAnchor(e.currentTarget);
                    setDownloadMenuItemId("pretty");
                  }}
                >
                  <DownloadIcon />
                </IconContainer>
                <Menu
                  anchorEl={downloadMenuAnchor}
                  open={Boolean(downloadMenuAnchor) && downloadMenuItemId === "pretty"}
                  onClose={() => {
                    setDownloadMenuAnchor(null);
                    setDownloadMenuItemId(null);
                  }}
                >
                  <CustomMenuItem
                    onClick={() => {
                      setTestSuiteId(prettyItem.idtest_suite_results);
                      setDownloadType("Excel");
                      setEnableDownload(true);
                      setDownloadMenuAnchor(null);
                      setDownloadMenuItemId(null);
                    }}
                  >
                    <ListItemIcon>
                      <BackupTableOutlined />
                    </ListItemIcon>
                    <Typography>Download Excel</Typography>
                  </CustomMenuItem>
                  <CustomMenuItem
                    onClick={() => {
                      setTestSuiteId(prettyItem.idtest_suite_results);
                      setDownloadType("CSV");
                      setEnableDownload(true);
                      setDownloadMenuAnchor(null);
                      setDownloadMenuItemId(null);
                    }}
                  >
                    <ListItemIcon>
                      <DownloadIcon />
                    </ListItemIcon>
                    <Typography>Download CSV</Typography>
                  </CustomMenuItem>
                  <CustomMenuItem
                    onClick={() => {
                      setTestSuiteId(prettyItem.idtest_suite_results);
                      setDownloadType("PDF");
                      setEnableDownload(true);
                      setDownloadMenuAnchor(null);
                      setDownloadMenuItemId(null);
                    }}
                  >
                    <ListItemIcon>
                      <PictureAsPdf />
                    </ListItemIcon>
                    <Typography>Download PDF</Typography>
                  </CustomMenuItem>
                </Menu>
                <IconContainer tooltipText={"View Log File"}>
                  <Link
                    component="button"
                    type="button"
                    onClick={() => handleOpenLogFile(prettyItem)}
                    sx={{ cursor: "pointer", display: "flex", alignItems: "center" }}
                  >
                    <LogFileIcon />
                  </Link>
                </IconContainer>
                <IconContainer tooltipText={"View test suite results "}>
                  <Link
                    target="_blank"
                    sx={{ cursor: "pointer" }}
                    onClick={(e) => handleTestCaseHistory(prettyItem, "ALL")}
                  >
                    <HistoryIcon />
                  </Link>
                </IconContainer>
                <IconContainer tooltipText={"View Run History"}>
                  <Link
                    target="_blank"
                    sx={{ cursor: "pointer" }}
                    onClick={(e) => handleViewRunHistory(prettyItem)}
                  >
                    <RunCircleOutlined />
                  </Link>
                </IconContainer>

                {/* view failed test cases history */}
                <IconContainer
                  tooltipText={
                    prettyItem.failCount > 0 ? "View Failed Test Cases" : "No Failed Test Cases"
                  }
                  handleClick={() => {
                    if (prettyItem.failCount > 0) {
                      handleTestCaseHistory(prettyItem, "FAIL");
                    }
                  }}
                  style={{
                    opacity: prettyItem.failCount > 0 ? 1 : 0.3,
                    cursor: prettyItem.failCount > 0 ? "pointer" : "not-allowed",
                  }}
                >
                  <ErrorOutlineIcon color={prettyItem.failCount > 0 ? "error" : "disabled"} />
                </IconContainer>

                {/* view benchmark report */}
                {prettyItem.benchmarkscenarios === true && (
                  <IconContainer
                    tooltipText={"View Benchmark Report"}
                    handleClick={() => {
                      handleBenchmarkScenarios(prettyItem);
                    }}
                    style={{
                      cursor: "pointer",
                    }}
                  >
                    <CompareRoundedIcon />
                  </IconContainer>
                )}
              </Box>
            </StyledTableBodyCell>
          )}
          <Box display="flex" alignItems={"center"}>
            <Typography>Switch View</Typography>
            <Switch onChange={() => setIsPrettyView((prev) => !prev)} checked={isPretty} />
          </Box>
        </Box>
      </Box>
      {isPretty && (
        <Box
          ref={componentRef}
          p={2}
          sx={{
            flex: 1,
            overflowY: "auto",
            minHeight: 0,
          }}
        >
          {isLoadingResults && !hasData ? (
            <Box display="flex" alignItems="center" justifyContent="center" height="100%">
              <CircularProgress />
            </Box>
          ) : hasData && prettyItem ? (
            <PrettyReports data={prettyItem} testSuiteName={testSuiteName} products={prod} />
          ) : (
            <Box display="flex" alignItems="center" justifyContent="center" height="100%">
              <Typography>No data available</Typography>
            </Box>
          )}
        </Box>
      )}
      {!isPretty && (
        <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
          {isLoadingResults && !hasData ? (
            <Box display="flex" alignItems="center" justifyContent="center" height="100%">
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer sx={{ height: "100%" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Run Date</StyledTableCell>
                    <StyledTableCell>Run By</StyledTableCell>
                    <StyledTableCell>Run Duration</StyledTableCell>
                    <StyledTableCell>Log File</StyledTableCell>
                    <StyledTableCell>Status</StyledTableCell>
                    <StyledTableCell>Total/Pass/Fail Count</StyledTableCell>
                    <StyledTableCell>Status Type</StyledTableCell>
                    <StyledTableCell>Actions</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.length ? (
                    data.map((item, index) => (
                      <TableRow key={index}>
                        <StyledTableBodyCell>{item.Executed_Date}</StyledTableBodyCell>
                        <StyledTableBodyCell>{item.Executed_By}</StyledTableBodyCell>
                        <StyledTableBodyCell>{item.Duration}</StyledTableBodyCell>
                        <StyledTableBodyCell>
                          <AuthedFileLink
                            url={item.logfile}
                            filename={`${item.testsuitename || testSuiteName || "test-suite"}.log`}
                          >
                            Log File
                          </AuthedFileLink>
                        </StyledTableBodyCell>
                        <StyledTableBodyCell>
                          <StatusPill
                            label={ExeReportHistoryStatus[item.Status]}
                            type={item.Status}
                          />
                        </StyledTableBodyCell>
                        <StyledTableBodyCell>
                          {item.totalCount}/{item.passCount}/{item.failCount}
                        </StyledTableBodyCell>
                        <StyledTableBodyCell>
                          <CustomSelect
                            options={TestSuiteStatusOptions}
                            width={"100%"}
                            background={"#ffffff"}
                            value={statusType[index] || "ALL"}
                            onChange={(e) => {
                              const { value } = e.target;
                              setStatusType((prev) => ({ ...prev, [index]: value }));
                              setSelectedStatus(value);
                            }}
                          />
                        </StyledTableBodyCell>
                        <StyledTableBodyCell>
                          <Tooltip
                            title={
                              ignoredStatuses[item.idtest_suite_results]
                                ? "Click to unignore"
                                : "Click to ignore"
                            }
                          >
                            <Switch
                              checked={!!ignoredStatuses[item.idtest_suite_results]}
                              onChange={() =>
                                handleToggleIgnored(
                                  item.idtest_suite_results,
                                  ignoredStatuses[item.idtest_suite_results],
                                )
                              }
                              color="primary"
                            />
                          </Tooltip>
                        </StyledTableBodyCell>
                        <StyledTableBodyCell>
                          <Box display={"flex"} columnGap={1}>
                            {item.Status !== "STARTED" &&
                              item.Status !== "RUNNING" &&
                              item.testSuiteId !== "" && (
                                <IconContainer
                                  tooltipText={"Execute"}
                                  handleClick={() => handleOpenDialog(item)}
                                >
                                  <CurrentExecutionIcon />
                                </IconContainer>
                              )}
                            {(item.Status === "STARTED" || item.Status === "RUNNING") && (
                              <IconContainer
                                tooltipText={"Stop"}
                                handleClick={() => handleStopExecution(item)}
                              >
                                <DoNotDisturbAltIcon />
                              </IconContainer>
                            )}
                            {(item.Status === "STARTED" || item.Status === "RUNNING") && (
                              <IconContainer
                                tooltipText={"Pause"}
                                handleClick={() =>
                                  handlePauseResumeTestSuite(item.idtest_suite_results, "pause")
                                }
                              >
                                <PauseIcon />
                              </IconContainer>
                            )}
                            {item.Status === "PAUSED" && (
                              <IconContainer
                                tooltipText={"Resume"}
                                handleClick={() =>
                                  handlePauseResumeTestSuite(item.idtest_suite_results, "resume")
                                }
                              >
                                <PlayArrowIcon />
                              </IconContainer>
                            )}
                            <IconContainer
                              tooltipText={"Download Options"}
                              handleClick={(e) => {
                                setDownloadMenuAnchor(e.currentTarget);
                                setDownloadMenuItemId(item.idtest_suite_results);
                              }}
                            >
                              <DownloadIcon />
                            </IconContainer>
                            <Menu
                              anchorEl={downloadMenuAnchor}
                              open={
                                Boolean(downloadMenuAnchor) &&
                                downloadMenuItemId === item.idtest_suite_results
                              }
                              onClose={() => {
                                setDownloadMenuAnchor(null);
                                setDownloadMenuItemId(null);
                              }}
                            >
                              <CustomMenuItem
                                onClick={() => {
                                  setTestSuiteId(item.idtest_suite_results);
                                  setDownloadType("Excel");
                                  setEnableDownload(true);
                                  setDownloadMenuAnchor(null);
                                  setDownloadMenuItemId(null);
                                }}
                              >
                                <ListItemIcon>
                                  <BackupTableOutlined />
                                </ListItemIcon>
                                <Typography>Download Excel</Typography>
                              </CustomMenuItem>
                              <CustomMenuItem
                                onClick={() => {
                                  setTestSuiteId(item.idtest_suite_results);
                                  setDownloadType("CSV");
                                  setEnableDownload(true);
                                  setDownloadMenuAnchor(null);
                                  setDownloadMenuItemId(null);
                                }}
                              >
                                <ListItemIcon>
                                  <DownloadIcon />
                                </ListItemIcon>
                                <Typography>Download CSV</Typography>
                              </CustomMenuItem>
                              <CustomMenuItem
                                onClick={() => {
                                  setTestSuiteId(item.idtest_suite_results);
                                  setDownloadType("PDF");
                                  setEnableDownload(true);
                                  setDownloadMenuAnchor(null);
                                  setDownloadMenuItemId(null);
                                }}
                              >
                                <ListItemIcon>
                                  <PictureAsPdf />
                                </ListItemIcon>
                                <Typography>Download PDF</Typography>
                              </CustomMenuItem>
                            </Menu>
                            <IconContainer tooltipText={"View Log File"}>
                              <Link
                                component="button"
                                type="button"
                                onClick={() => handleOpenLogFile(item)}
                                sx={{ cursor: "pointer", display: "flex", alignItems: "center" }}
                              >
                                <LogFileIcon />
                              </Link>
                            </IconContainer>
                            <IconContainer tooltipText={"View test suite results "}>
                              <Link
                                target="_blank"
                                sx={{ cursor: "pointer" }}
                                onClick={(e) =>
                                  handleTestCaseHistory(item, statusType[index] || "ALL")
                                }
                              >
                                <HistoryIcon />
                              </Link>
                            </IconContainer>
                            <IconContainer tooltipText={"View Run History"}>
                              <Link
                                target="_blank"
                                sx={{ cursor: "pointer" }}
                                onClick={(e) => handleViewRunHistory(item)}
                              >
                                <RunCircleOutlined />
                              </Link>
                            </IconContainer>

                            {/* view failed test cases history */}
                            <IconContainer
                              tooltipText={
                                item.failCount > 0
                                  ? "View Failed Test Cases"
                                  : "No Failed Test Cases"
                              }
                              handleClick={() => {
                                if (item.failCount > 0) {
                                  handleTestCaseHistory(item, "FAIL");
                                }
                              }}
                              style={{
                                opacity: item.failCount > 0 ? 1 : 0.3,
                                cursor: item.failCount > 0 ? "pointer" : "not-allowed",
                              }}
                            >
                              <ErrorOutlineIcon color={item.failCount > 0 ? "error" : "disabled"} />
                            </IconContainer>

                            <IconContainer
                              tooltipText={"View Report"}
                              handleClick={() => {
                                setPrettyItem(item);
                                setIsPrettyView(true);
                              }}
                            >
                              <AssessmentOutlinedIcon />
                            </IconContainer>
                          </Box>
                        </StyledTableBodyCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <StyledTableBodyCell align="center" colSpan={9}>
                        No data available
                      </StyledTableBodyCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}
      {selectedItemForExecute && openDialog && (
        <TestSuiteExecuteDialog
          isExecuteTestSuiteSuccess={isExecuteTestSuiteSuccess}
          handleExecuteTestSuite={handleExecuteTestSuite}
          item={selectedItemForExecute}
          openDialog={openDialog}
          handleCloseDialog={handleCloseDialog}
          statusType={selectedStatus}
        />
      )}
      <CustomDialog
        open={openStopDialog}
        onClose={handleCancelStopExecution}
        title="Stop Execution"
        content={<Typography>Are you sure you want to stop execution of this Suite?</Typography>}
        actions={
          <>
            <Button onClick={handleConfirmStopExecution}>Yes</Button>
            <Button onClick={handleCancelStopExecution}>No</Button>
          </>
        }
      />
      {!isPretty && (
        <Pagination
          sx={{ display: "flex", justifyContent: "center", mt: 1, flexShrink: 0 }}
          page={pageNO}
          onChange={(event, value) => {
            setPageno(value);
          }}
          count={totalPageno}
        />
      )}
    </Box>
  );
};

export default ExecutionReportsTestSuiteTable;
